import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Button, ConfirmationModal } from '@components/common'
import MarketingScheduleCampaignModal from '../../MarketingScheduleCampaignModal.component'
import './AdvancedSendStyle.css'
import { format } from 'date-fns'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { generateCampaignMessages } from '@utils/marketing.utils'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import {
  sendCampaignSms,
  sendCampaignEmail,
  updateCampaign,
} from '@store/actions'
import {
  isEmailBackendEnabled,
  requestGoogleWorkspaceStatus,
} from '@services/api/email.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'

type AdvancedSendProps = {
  show: boolean
  onHide: () => void
  startDate: string | Date
  selectedCampaign: ICampaignRequest
}

const AdvancedSendModal: React.FC<AdvancedSendProps> = ({
  show,
  onHide,
  startDate,
  selectedCampaign,
}) => {
  const dispatch = useAppDispatch()
  const [isSending, setIsSending] = useState<boolean>(false)
  const [googleMailboxConnected, setGoogleMailboxConnected] = useState(true)
  const { loading, error } = useAppSelector((state) => state.marketing)
  const { showToast } = useNotificationToast()

  const isLoading = loading.add || loading.delete || loading.update
  const errorMessage = error.add || error.delete || error.update
  const isEmailCampaign = selectedCampaign.channel === 'Email'
  const emailSendBlocked = isEmailCampaign && !googleMailboxConnected

  useEffect(() => {
    if (!show || !isEmailCampaign) return
    if (!isEmailBackendEnabled()) {
      setGoogleMailboxConnected(false)
      return
    }
    void requestGoogleWorkspaceStatus()
      .then((status) => {
        setGoogleMailboxConnected(Boolean(status?.isConnected))
      })
      .catch(() => {
        setGoogleMailboxConnected(false)
      })
  }, [show, isEmailCampaign, selectedCampaign.clinic_id])

  const handleConfirm = async (): Promise<void> => {
    if (emailSendBlocked) {
      showToast(
        createFailedToast(
          'Google email',
          'Connect a Google mailbox under Settings > Communications > Email before sending.',
        ),
      )
      return
    }
    setIsSending(true)
    const campaignMessages = generateCampaignMessages({
      ...selectedCampaign,
      sending_option: 'Immediately',
    })
    if (campaignMessages.length > 0) {
      const sendAction = isEmailCampaign
        ? sendCampaignEmail(campaignMessages)
        : sendCampaignSms(campaignMessages)
      await dispatch(sendAction).then(() => {
        if (selectedCampaign.campaign_id)
          dispatch(
            updateCampaign({
              campaign_id: selectedCampaign.campaign_id,
              data: {
                message_status: 'Sent',
              },
            }),
          )
      })
    }
  }

  const launchDateLabel = format(new Date(startDate), 'MMMM dd, yyyy')

  const renderResultBody = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className="confirmation-modal__body advanced-send-modal__status">
          <Spinner
            animation="border"
            variant="primary"
            role="status"
            className="appt-spinner"
          />
          <p className="confirmation-modal__message">Sending campaign...</p>
        </div>
      )
    }

    if (errorMessage) {
      return (
        <div className="confirmation-modal__body advanced-send-modal__status">
          <i className="appt-failed mgc_close_circle_fill" />
          <p className="confirmation-modal__title">Sending failed</p>
          <p className="confirmation-modal__message">
            Sending campaign failed.
          </p>
        </div>
      )
    }

    return (
      <div className="confirmation-modal__body advanced-send-modal__status">
        <i
          className="ti ti-circle-check-filled"
          style={{ fontSize: '64px', color: '#3E8E53' }}
        />
        <p className="confirmation-modal__title">Campaign sent</p>
        <p className="confirmation-modal__message">
          Campaign successfully sent.
        </p>
      </div>
    )
  }

  return (
    <>
      <MarketingScheduleCampaignModal
        show={show && !isSending}
        launchDateLabel={launchDateLabel}
        confirmDisabled={emailSendBlocked}
        extraDescription={
          emailSendBlocked
            ? 'Connect a Google mailbox in Email settings.'
            : undefined
        }
        onCancel={onHide}
        onConfirm={handleConfirm}
      />
      <ConfirmationModal
        show={show && isSending}
        onHide={onHide}
        title="Advanced send"
        className="confirmation-modal--alert advanced-send-modal"
        headerClassName="confirmation-modal__header"
        size="md"
        body={renderResultBody()}
        footer={
          <div className="confirmation-modal__footer">
            <Button className="button-primary" size="md" onClick={onHide}>
              Got it
            </Button>
          </div>
        }
      />
    </>
  )
}

export default AdvancedSendModal
